import { useMemo } from "react"
import { type Query as TCubeQuery } from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"

const CUBE = "metrics"
const SPEND_MEASURE = `${CUBE}.total_direct_spend`
const DATE_DIMENSION = `${CUBE}.date`

// Daily-spend threshold above which we suggest two updates per week.
const HIGH_SPEND_DAILY_THRESHOLD = 5000
const LOOKBACK_DAYS = 30

export interface UpdatesPerWeekRecommendation {
  recommendedUpdatesPerWeek: 1 | 2
  averageDailySpend: number
  isLoading: boolean
}

/**
 * Recommends how many budget updates per week a user should make, based on their
 * average daily direct spend over the trailing 30 days. Over $5k/day -> 2, else 1.
 */
export function useUpdatesPerWeekRecommendation(): UpdatesPerWeekRecommendation {
  const currentCompany = useCurrentCompany()

  const query = useMemo<TCubeQuery>(() => {
    const endDate = new Date()
    const startDate = new Date(endDate)
    startDate.setDate(startDate.getDate() - LOOKBACK_DAYS)

    const toISODate = (date: Date) => date.toISOString().slice(0, 10)

    return {
      measures: [SPEND_MEASURE],
      timeDimensions: [
        {
          dimension: DATE_DIMENSION,
          granularity: "day",
          dateRange: [toISODate(startDate), toISODate(endDate)],
        },
      ],
    }
  }, [currentCompany?.id])

  const { resultSet, isLoading } = useCubeQuery(query)

  const averageDailySpend = useMemo(() => {
    if (!resultSet) {
      return 0
    }
    const rows = resultSet.rawData() ?? []
    if (rows.length === 0) {
      return 0
    }
    const totalSpend = rows.reduce((sum, row) => {
      const value = Number(row[SPEND_MEASURE] ?? 0)
      return sum + (Number.isFinite(value) ? value : 0)
    }, 0)
    return totalSpend / LOOKBACK_DAYS
  }, [resultSet])

  return {
    recommendedUpdatesPerWeek:
      averageDailySpend > HIGH_SPEND_DAILY_THRESHOLD ? 2 : 1,
    averageDailySpend,
    isLoading,
  }
}
